
import { PAGES } from "@consts";

export default {
	name: "HintsSection",
	props: {
		hints: {
			type: Array,
			required: true,
		},
		dateCode: {
			type: String,
			required: true,
			validator(val) {
				return /^\d{6}$/.test(val);
			},
		},
		isParseword: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			localHints: [],
		};
	},
	computed: {
		localStorageKey() {
			if (this.isParseword) {
				return "WFW_PARSEWORD";
			}
			return this.$route.name === PAGES.WORDLE_YESTERDAY_HINTS
				? "WFW_WORDLE_YESTERDAY_HINTS"
				: "WFW_WORDLE_HINTS";
		},
	},
	created() {
		if (this.isParseword) {
			this.localHints = this.hints.map((hint, index) => ({
				hintBefore: hint.before,
				hintAfter: hint.after,
				hintType: hint.type,
				hintIndicator: hint.indicator,
				showHint: false,
				disabled: index !== 0,
				buttonText: this.getButtonText(index),
			}));

			return;
		}

		this.localHints = this.hints.map((hint, index) => ({
			hintText: hint,
			showHint: false,
			disabled: index !== 0,
			buttonText: this.getButtonText(index),
		}));
	},
	mounted() {
		this.loadState();
	},
	methods: {
		onButtonClick(index) {
			this.localHints[index].showHint = true;

			if (index + 1 < this.localHints.length) {
				this.localHints[index + 1].disabled = false;
			}

			this.$store.dispatch("tracking/trackEvent", {
				eventCategory: this.isParseword ? "Parseword" : "Wordle Hints",
				eventAction: `Show hint ${index + 1}`,
			});

			this.saveState();
		},
		getButtonText(index) {
			const numberAsWord = this.convertNumberToWords(index + 1);

			if (this.isParseword) {
				return numberAsWord
					? `Parseword&nbsp;Step&nbsp;${this.capitalizeFirstLetter(
							numberAsWord
					  )}`
					: `Show me the step`;
			}
			return numberAsWord
				? `Wordle Hint ${this.capitalizeFirstLetter(numberAsWord)}`
				: `Show me the hint`;
		},
		saveState() {
			if (process.server) {
				return;
			}

			const key = this.localStorageKey;

			const states = this.localHints
				.map((hint) => (hint.showHint ? "1" : "0"))
				.join("");

			const saveString = `${this.dateCode}_${states}`;
			window.localStorage.setItem(key, saveString);
		},
		loadState() {
			if (process.server) {
				return;
			}

			const key = this.localStorageKey;

			const data = window.localStorage.getItem(key);
			if (!data) {
				return;
			}

			const regex = /^(\d{6})_([01]+)$/;
			if (!regex.test(data)) {
				window.localStorage.removeItem(key);
				return;
			}

			const [, dateCode, states] = data.match(regex);
			if (dateCode !== this.dateCode) {
				window.localStorage.removeItem(key);
				return;
			}

			if (states.length !== this.hints.length) {
				window.localStorage.removeItem(key);
				return;
			}

			states.split("").forEach((state, i) => {
				if (state === "1") {
					this.localHints[i].showHint = true;
					this.localHints[i].disabled = false;

					if (i + 1 < this.localHints.length) {
						this.localHints[i + 1].disabled = false;
					}
				}
			});
		},
		capitalizeFirstLetter(val) {
			return String(val).charAt(0).toUpperCase() + String(val).slice(1);
		},
		convertNumberToWords(number) {
			const numbersToWords = {
				0: "zero",
				1: "one",
				2: "two",
				3: "three",
				4: "four",
				5: "five",
				6: "six",
				7: "seven",
				8: "eight",
				9: "nine",
				10: "ten",
				11: "eleven",
				12: "twelve",
				13: "thirteen",
				14: "fourteen",
				15: "fifteen",
				16: "sixteen",
				17: "seventeen",
				18: "eighteen",
				19: "nineteen",
				20: "twenty",
				30: "thirty",
				40: "forty",
				50: "fifty",
				60: "sixty",
				70: "seventy",
				80: "eighty",
				90: "ninety",
			};
			// if number present in object no need to go further
			if (number in numbersToWords) {
				return numbersToWords[number];
			}

			// Initialize the words variable to an empty string
			let words = "";

			// If the number is greater than or equal to 100, handle the hundreds place (ie, get the number of hundres)
			if (number >= 100) {
				// Add the word form of the number of hundreds to the words string
				words += `${this.convertNumberToWords(
					Math.floor(number / 100)
				)} hundred`;

				// Remove the hundreds place from the number
				number %= 100;
			}

			// If the number is greater than zero, handle the remaining digits
			if (number > 0) {
				// If the words string is not empty, add "and"
				if (words !== "") {
					words += " and ";
				}

				// If the number is less than 20, look up the word form in the numbersToWords object
				if (number < 20) {
					words += numbersToWords[number];
				} else {
					// Otherwise, add the word form of the tens place to the words string
					// if number = 37, Math.floor(number /10) will give you 3 and 3 * 10 will give you 30
					words += numbersToWords[Math.floor(number / 10) * 10];

					// If the ones place is not zero, add the word form of the ones place
					if (number % 10 > 0) {
						words += `-${numbersToWords[number % 10]}`;
					}
				}
			}

			// Return the word form of the number
			return words;
		},
	},
};
